import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const SERIES = [
  { key: 'caudal_gas',  label: 'Caudal gas',     color: '#d97706', unit: 'm³/h' },
  { key: 'caudal_pet',  label: 'Caudal petróleo', color: '#ea580c', unit: 'm³/h' },
  { key: 'caudal_agua', label: 'Caudal agua',     color: '#0891b2', unit: 'm³/h' },
  { key: 'pres_gas',    label: 'Pres. entrada',   color: '#7c3aed', unit: 'kgf/cm²' },
  { key: 'pres_salida', label: 'Pres. salida',    color: '#db2777', unit: 'kgf/cm²' },
  { key: 'temp',        label: 'Temp. sep.',      color: '#2563eb', unit: '°C' },
  { key: 'temp_gas',    label: 'Temp. gas sal.',  color: '#16a34a', unit: '°C' },
  { key: 'temp_pet',    label: 'Temp. pet. sal.', color: '#dc2626', unit: '°C' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:'#ffffff', border:'1px solid #e0e0e0',
      borderRadius:'8px', padding:'10px 14px', fontSize:'11px',
      boxShadow:'0 4px 12px rgba(0,0,0,0.08)'
    }}>
      <div style={{color:'#6b6b6b', marginBottom:'6px'}}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{color:p.color, marginBottom:'2px'}}>
          {p.name}: <b>{p.value != null ? Number(p.value).toFixed(2) : '—'}</b>
          {' '}{SERIES.find(s => s.key === p.dataKey)?.unit}
        </div>
      ))}
    </div>
  );
};

function filtrarOutliers(data, keys) {
  if (data.length < 4) return data;
  const stats = {};
  keys.forEach(k => {
    const vals = data.map(d => d[k]).filter(v => v != null && v !== 0);
    if (vals.length < 2) return;
    const mean = vals.reduce((a,b) => a+b, 0) / vals.length;
    const std  = Math.sqrt(vals.reduce((a,b) => a + (b-mean)**2, 0) / vals.length);
    stats[k] = { mean, std };
  });
  return data.map(d => {
    const clean = { ...d };
    keys.forEach(k => {
      if (!stats[k] || d[k] == null) return;
      const { mean, std } = stats[k];
      if (std > 0 && Math.abs(d[k] - mean) > 3 * std) clean[k] = null;
    });
    return clean;
  });
}

export default function GraficoSeparador({ sepId, nombre, onClose }) {
  const [data,    setData]    = useState([]);
  const [horas,   setHoras]   = useState(1);
  const [activas, setActivas] = useState(['caudal_gas','caudal_pet','temp']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`/api/separadores/${sepId}/mediciones?horas=${horas}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        const keys = ['caudal_gas','caudal_pet','caudal_agua','pres_gas','pres_salida','temp','temp_gas','temp_pet'];
        setData(filtrarOutliers(d, keys));
      } catch(e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const iv = setInterval(fetchData, 300000);
    return () => clearInterval(iv);
  }, [sepId, horas]);

  const toggleSerie = (key) => {
    setActivas(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.25)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000, fontFamily:'Inter,-apple-system,sans-serif'
    }}>
      <div style={{
        background:'#ffffff', border:'1px solid #e0e0e0',
        borderRadius:'12px', width:'90vw', maxWidth:'1100px',
        maxHeight:'90vh', overflow:'auto', padding:'20px',
        boxShadow:'0 8px 40px rgba(0,0,0,0.10)'
      }}>

        {/* Header */}
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
          <div>
            <div style={{fontSize:'16px', fontWeight:'600', color:'#1a1a1a'}}>{nombre}</div>
            <div style={{fontSize:'11px', color:'#a0a0a0', fontFamily:'monospace'}}>{sepId} — Tendencias históricas</div>
          </div>
          <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
            {[1,6,12,24,48].map(h => (
              <button key={h} onClick={() => setHoras(h)} style={{
                padding:'4px 10px', borderRadius:'6px', fontSize:'11px', cursor:'pointer',
                border: horas===h ? '1px solid #1a1a1a' : '1px solid #e0e0e0',
                background: horas===h ? '#1a1a1a' : '#f5f5f5',
                color: horas===h ? '#ffffff' : '#6b6b6b'
              }}>{h}h</button>
            ))}
            <button onClick={onClose} style={{
              padding:'4px 10px', borderRadius:'6px', fontSize:'11px', cursor:'pointer',
              border:'1px solid #e0e0e0', background:'#f5f5f5', color:'#6b6b6b', marginLeft:'8px'
            }}>✕ Cerrar</button>
          </div>
        </div>

        {/* Toggle series */}
        <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'14px'}}>
          {SERIES.map(s => (
            <button key={s.key} onClick={() => toggleSerie(s.key)} style={{
              padding:'3px 10px', borderRadius:'20px', fontSize:'10px', cursor:'pointer',
              border: `1px solid ${activas.includes(s.key) ? s.color : '#e0e0e0'}`,
              background: activas.includes(s.key) ? `${s.color}18` : 'transparent',
              color: activas.includes(s.key) ? s.color : '#a0a0a0',
              fontWeight: activas.includes(s.key) ? '600' : '400'
            }}>{s.label}</button>
          ))}
        </div>

        {/* Gráfico */}
        {loading ? (
          <div style={{textAlign:'center', color:'#a0a0a0', padding:'60px 0'}}>Cargando datos...</div>
        ) : data.length === 0 ? (
          <div style={{textAlign:'center', color:'#a0a0a0', padding:'60px 0'}}>
            Sin datos en las últimas {horas}h — el monitor registra cada 5 minutos
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={data} margin={{top:5, right:20, left:0, bottom:5}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis
                dataKey="ts"
                tick={{fill:'#a0a0a0', fontSize:9}}
                tickLine={{stroke:'#e0e0e0'}}
                axisLine={{stroke:'#e0e0e0'}}
                interval={Math.max(0, Math.floor(data.length / 8) - 1)}
                angle={-25}
                textAnchor="end"
                height={40}
                label={{value:'hora AR', position:'insideBottomRight', offset:-5, fill:'#a0a0a0', fontSize:8}}
              />
              <YAxis
                tick={{fill:'#a0a0a0', fontSize:10}}
                tickLine={{stroke:'#e0e0e0'}}
                axisLine={{stroke:'#e0e0e0'}}
                width={45}
              />
              <Tooltip content={<CustomTooltip/>}/>
              <Legend
                wrapperStyle={{fontSize:'10px', paddingTop:'12px'}}
                formatter={(value) => <span style={{color:'#6b6b6b'}}>{value}</span>}
              />
              {SERIES.filter(s => activas.includes(s.key)).map(s => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={1.5}
                  dot={false}
                  connectNulls={true}
                  label={(props) => {
                    const { index, x, y, value } = props;
                    if (index !== data.length - 1) return null;
                    if (value == null) return null;
                    return (
                      <text x={x+4} y={y} fill={s.color} fontSize={9} fontWeight="600" textAnchor="start">
                        {Number(value).toFixed(1)}
                      </text>
                    );
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* Footer */}
        <div style={{
          marginTop:'12px', fontSize:'10px', color:'#a0a0a0',
          display:'flex', justifyContent:'space-between'
        }}>
          <span>{data.length} registros — actualización cada 5 minutos</span>
          <span>SepManager — Rocket Tech S.A.S</span>
        </div>
      </div>
    </div>
  );
}
